import moment from 'moment';
import React, { forwardRef, Ref } from 'react';
import ReactDatePicker from 'react-datepicker';
import { FieldType } from '../../utils/apollo/graphqlSchema';
import DatePicker from './DatePicker';
import DateTimePicker from './DateTimePicker';
import MonthPicker from './MonthPicker';
import WeekPicker from './WeekPicker';
import YearPicker from './YearPicker';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { toJavaScriptDate } from '../Builder/DatePickerWidget/utils';
import { END_DATE_CLASS_NAME } from '../Builder/DatePickerWidget/DateRangePicker/DateRangePickerUI';
import { assertNotNil } from '../../utils/types';
import { isNil } from 'lodash';
import { globalFormat, isDateValid } from '../../utils/formatting';
import {
  displayDateFormatDictionary,
  displayTimeFormatDictionary,
} from './DatePicker/utils';

interface IProps {
  fieldType: FieldType;
  onChange: (dateString: string) => void;
  value: string;
  placeholderText?: string;
  autoFocus?: boolean;
  isBetween?: boolean;
  hasError?: boolean;
}

export default forwardRef(
  (
    {
      fieldType,
      onChange,
      value,
      placeholderText,
      autoFocus,
      isBetween,
      hasError,
    }: IProps,
    ref: Ref<ReactDatePicker>,
  ) => {
    const defaultConfig = useBootstrapSelector('defaultConfig');
    assertNotNil(defaultConfig);
    const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
    const highlightDates = [
      {
        [END_DATE_CLASS_NAME]: [toJavaScriptDate(defaultConfig.defaultEnd)],
      },
    ];
    const convertDateToString = (date: Date, format: string) =>
      moment(date).format(format).toString();

    const convertDateStringToDate = (
      dateString?: string,
      dateFormat?: string,
    ) => {
      if (!dateString) {
        return toJavaScriptDate(defaultConfig?.defaultEnd);
      } else {
        return moment(dateString, dateFormat).toDate();
      }
    };

    const onChangeDateToString = (date: Date | null, format: string) => {
      if (isNil(date) || !isDateValid(date)) {
        // do not propagate parent's onChange if the date is invalid
        return null;
      }
      const dateString = convertDateToString(date, format);
      onChange(dateString);
    };

    switch (fieldType) {
      case FieldType.Date:
        return (
          <DatePicker
            ref={ref}
            dateFormat={displayDateFormatDictionary[dateFormat][FieldType.Date]}
            // discrepancy between our date format & moment, moment expects date in MM/DD/YYYY
            onChange={(date) => onChangeDateToString(date, 'MM/DD/YYYY')}
            selectedValue={
              value ? convertDateStringToDate(value, 'MM/DD/YYYY') : null
            }
            placeholderText={placeholderText}
            autoFocus={autoFocus}
            highlightDates={highlightDates}
            liveDate={toJavaScriptDate(defaultConfig.defaultEnd)}
            hasError={hasError}
          />
        );
      case FieldType.Timestamp:
      case FieldType.Datetime:
        return (
          <DateTimePicker
            ref={ref}
            placeholderText={placeholderText}
            dateFormat={
              `${displayDateFormatDictionary[dateFormat][fieldType]}` +
              ` ${displayTimeFormatDictionary[timeFormat]}`
            }
            onChange={(date) =>
              onChangeDateToString(date, 'MM/DD/YYYY HH:mm:ss')
            }
            selectedValue={convertDateStringToDate(
              value,
              'MM/DD/YYYY HH:mm:ss',
            )}
            autoFocus={autoFocus}
            hasError={hasError}
          />
        );
      case FieldType.Week:
        return (
          <WeekPicker
            ref={ref}
            placeholderText={placeholderText}
            dateFormat={displayDateFormatDictionary[dateFormat][FieldType.Week]}
            onChange={(date) => onChangeDateToString(date, 'MM/DD/YYYY')}
            selectedValue={convertDateStringToDate(value, 'MM/DD/YYYY')}
            autoFocus={autoFocus}
            isBetween={isBetween}
            hasError={hasError}
          />
        );
      case FieldType.Month:
        return (
          <MonthPicker
            ref={ref}
            placeholderText={placeholderText}
            dateFormat={
              displayDateFormatDictionary[dateFormat][FieldType.Month]
            }
            onChange={(date) => onChangeDateToString(date, 'MM/yyyy')}
            selectedValue={convertDateStringToDate(value, 'MM/yyyy')}
            autoFocus={autoFocus}
            hasError={hasError}
          />
        );
      case FieldType.Year:
        return (
          <YearPicker
            ref={ref}
            placeholderText={placeholderText}
            dateFormat={displayDateFormatDictionary[dateFormat][FieldType.Year]}
            onChange={(date) => onChangeDateToString(date, 'yyyy')}
            selectedValue={convertDateStringToDate(value, 'yyyy')}
            autoFocus={autoFocus}
            hasError={hasError}
          />
        );
      default:
        return null;
    }
  },
);
